import React, { FC, ReactNode, PointerEvent } from "react";
import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { styled } from "@mui/system";

import { COLORS, SPACINGS, RADII } from "../../common/consts";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Button, { ButtonProps } from "./Button";
import Text, { TextVariant } from "./Text";

interface DialogProps extends Omit<MuiDialogProps, "children" | "onClose" | "maxWidth"> {
  title?: string;
  children: ReactNode;
  primaryButtonProps?: ButtonProps;
  secondaryButtonProps?: ButtonProps;
  onClose?: () => void;
  maxWidth?: string | number;
}

const StyledDialog = styled(MuiDialog)`
  .MuiDialog-paper {
    border-radius: ${RADII.lg};
    margin: ${SPACINGS.md};
    max-width: calc(100% - ${SPACINGS.md} * 2);
    max-height: calc(100% - ${SPACINGS.md} * 2);
    background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}) !important;
    color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary}) !important;
    transition: background-color 0.3s ease, color 0.3s ease;
    width: auto;
    
    @media (max-width: 768px) {
      margin: ${SPACINGS.sm};
      max-width: calc(100% - ${SPACINGS.sm} * 2) !important;
      max-height: calc(100% - ${SPACINGS.sm} * 2);
      width: calc(100% - ${SPACINGS.sm} * 2) !important;
    }
  }

  .MuiDialogContent-root {
    padding: ${SPACINGS.lg} ${SPACINGS.xxl} ${SPACINGS.xl} ${SPACINGS.xxl};
    
    @media (max-width: 768px) {
      padding: ${SPACINGS.md} ${SPACINGS.lg};
    }
  }

  .MuiDialogActions-root {
    padding: ${SPACINGS.xl} ${SPACINGS.xxl};
    
    @media (max-width: 768px) {
      padding: ${SPACINGS.md} ${SPACINGS.lg};
      flex-direction: column-reverse;
      gap: ${SPACINGS.sm};
      
      button {
        width: 100%;
      }
    }
  }
`;

interface DialogHeaderProps {
  isTitleExist: boolean;
}
const shouldForwardProp = makeShouldForwardProps<DialogHeaderProps>([
  "isTitleExist",
]);
const DialogHeader = styled("div", {
  shouldForwardProp,
})<DialogHeaderProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ isTitleExist }) =>
    isTitleExist ? "space-between" : "flex-end"};
  padding: ${SPACINGS.lg} ${SPACINGS.xxl} ${SPACINGS.lg} ${SPACINGS.xxl};
  flex-shrink: 0;
`;

const CloseIconWrapper = styled("div")`
  padding: ${SPACINGS.xs};
  margin-left: auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  flex-shrink: 0;

  &:hover {
    background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
  }
`;

const Dialog: FC<DialogProps> = ({
  title,
  open,
  children,
  onClose,
  primaryButtonProps,
  secondaryButtonProps,
  maxWidth,
  className,
  ...props
}) => {
  const handleClose = (
    _event: PointerEvent<HTMLDivElement>,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason && reason === "backdropClick") return;

    if (onClose) {
      onClose();
    }
  };
  const paperStyle = maxWidth ? {
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    width: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
  } : undefined;

  // Type assertion needed due to MUI styled component type conflicts
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dialogProps = {
    open,
    onClose: handleClose,
    className,
    ...props,
    PaperProps: {
      ...props.PaperProps,
      style: {
        ...props.PaperProps?.style,
        ...paperStyle,
      },
    },
  } as any;

  return (
    <StyledDialog {...dialogProps}>
      <DialogHeader isTitleExist={!!title}>
        {title && (
          <Text 
            variant={TextVariant.xl} 
            style={{ 
              margin: 0,
              fontFamily: '"Work Sans", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontWeight: 600,
              fontSize: "1.25rem",
              lineHeight: "1.43",
              color: `var(--color-foregroundPrimary, ${COLORS.foregroundPrimary})`
            }}
          >
            {title}
          </Text>
        )}
        <CloseIconWrapper onClick={onClose}>
          <CloseIcon
            width="24px"
            height="24px"
            color="#686873"
          />
        </CloseIconWrapper>
      </DialogHeader>
      <DialogContent>{children}</DialogContent>
      <DialogActions style={{ justifyContent: "flex-end", gap: SPACINGS.sm }}>
        {secondaryButtonProps && (
          <Button className="px-3" {...secondaryButtonProps} />
        )}
        {primaryButtonProps && (
          <Button className="px-3" {...primaryButtonProps} />
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default Dialog;
